import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import LayersIcon from '@mui/icons-material/Layers';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoIcon from '@mui/icons-material/Info';

const MappedInDataTest = ({ mapView, buttonColor = '#1a1a2e' }) => {
  const [loading, setLoading] = useState(false);
  const [buildingsData, setBuildingsData] = useState([]);
  const [locationsData, setLocationsData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('PROTO Hologram Display');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [error, setError] = useState('');

  // Extract buildings and floors data
  const extractBuildingsAndFloors = () => {
    if (!mapView || !mapView.currentMap) {
      setError('MapView not initialized');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const mapData = mapView.currentMap;
      const buildings = [];

      // Get all floor stacks (buildings)
      mapData.getByType('floor-stack').forEach((floorStack) => {
        const buildingInfo = {
          id: floorStack.id,
          name: floorStack.name,
          floors: [],
        };

        // Get all floors in this building
        floorStack.floors.forEach((floor) => {
          buildingInfo.floors.push({
            id: floor.id,
            name: floor.name,
            elevation: floor.elevation,
            shortName: floor.shortName || '',
          });
        });

        buildings.push(buildingInfo);
      });

      setBuildingsData(buildings);
      console.log('[MappedInDataTest] Buildings and Floors:', buildings);
    } catch (err) {
      console.error('[MappedInDataTest] Error extracting buildings:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Extract all enterprise locations
  const extractEnterpriseLocations = () => {
    if (!mapView || !mapView.currentMap) {
      setError('MapView not initialized');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const mapData = mapView.currentMap;
      const locations = [];

      // Get all enterprise locations
      mapData.getByType('enterprise-location').forEach((location) => {
        const locationInfo = {
          id: location.id,
          name: location.name,
          description: location.description || '',
          logo: location.logo || null,
          externalId: location.externalId || '',
          type: location.type || '',
          points: [],
          spaces: [],
          objects: [],
        };

        // Extract point information (includes floor and building)
        if (location.points && location.points.length > 0) {
          location.points.forEach((point) => {
            locationInfo.points.push({
              id: point.id,
              floorId: point.floor?.id || null,
              floorName: point.floor?.name || null,
              floorElevation: point.floor?.elevation || null,
              buildingId: point.floor?.floorStack?.id || null,
              buildingName: point.floor?.floorStack?.name || null,
              coordinates: point.coordinate ? {
                latitude: point.coordinate.latitude,
                longitude: point.coordinate.longitude,
              } : null,
            });
          });
        }

        // Extract space information
        if (location.spaces && location.spaces.length > 0) {
          location.spaces.forEach((space) => {
            locationInfo.spaces.push({
              id: space.id,
              name: space.name,
              floorId: space.floor?.id || null,
              floorName: space.floor?.name || null,
            });
          });
        }

        // Extract object information
        if (location.objects && location.objects.length > 0) {
          location.objects.forEach((obj) => {
            locationInfo.objects.push({
              id: obj.id,
              name: obj.name,
              floorId: obj.floor?.id || null,
              floorName: obj.floor?.name || null,
            });
          });
        }

        locations.push(locationInfo);
      });

      setLocationsData(locations);
      console.log('[MappedInDataTest] Enterprise Locations:', locations);
    } catch (err) {
      console.error('[MappedInDataTest] Error extracting locations:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Search for a specific location
  const searchLocation = () => {
    if (!mapView || !mapView.currentMap || !searchTerm) {
      setError('MapView not initialized or search term empty');
      return;
    }

    setLoading(true);
    setError('');
    setSelectedLocation(null);

    try {
      const mapData = mapView.currentMap;

      // Find the enterprise location by name
      const location = mapData.getByType('enterprise-location').find(
        (l) => l.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

      if (!location) {
        setError(`Location "${searchTerm}" not found`);
        setLoading(false);
        return;
      }

      const locationDetails = {
        id: location.id,
        name: location.name,
        description: location.description || 'No description',
        logo: location.logo || 'No logo',
        externalId: location.externalId || 'No external ID',
        type: location.type || 'Unknown type',
        metadata: {},
        pointsInfo: [],
      };

      // Get point information (floor and building)
      if (location.points && location.points.length > 0) {
        location.points.forEach((point, index) => {
          locationDetails.pointsInfo.push({
            index,
            pointId: point.id,
            floor: {
              id: point.floor?.id || null,
              name: point.floor?.name || null,
              elevation: point.floor?.elevation || null,
              shortName: point.floor?.shortName || null,
            },
            building: {
              id: point.floor?.floorStack?.id || null,
              name: point.floor?.floorStack?.name || null,
            },
            coordinates: point.coordinate ? {
              latitude: point.coordinate.latitude,
              longitude: point.coordinate.longitude,
            } : null,
          });
        });
      }

      setSelectedLocation(locationDetails);
      console.log('[MappedInDataTest] Selected Location Details:', locationDetails);
    } catch (err) {
      console.error('[MappedInDataTest] Error searching location:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Auto-extract data on mount if mapView is available
  useEffect(() => {
    if (mapView && mapView.currentMap) {
      extractBuildingsAndFloors();
      extractEnterpriseLocations();
    }
  }, [mapView]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: buttonColor }}>
        MappedIn Data Test Component
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!mapView && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          MapView not provided. Please pass the mapView prop to this component.
        </Alert>
      )}

      {/* Action Buttons */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <BusinessIcon />}
            onClick={extractBuildingsAndFloors}
            disabled={loading || !mapView}
            sx={{ bgcolor: buttonColor, '&:hover': { bgcolor: buttonColor, filter: 'brightness(0.9)' } }}
          >
            Extract Buildings & Floors
          </Button>

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LocationOnIcon />}
            onClick={extractEnterpriseLocations}
            disabled={loading || !mapView}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
          >
            Extract All Locations
          </Button>
        </Box>
      </Paper>

      {/* Search Location */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Search Specific Location
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            label="Location Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="e.g., PROTO Hologram Display"
            size="small"
          />
          <Button
            variant="contained"
            onClick={searchLocation}
            disabled={loading || !mapView || !searchTerm}
            sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' }, minWidth: 120 }}
          >
            Search
          </Button>
        </Box>
      </Paper>

      {/* Selected Location Details */}
      {selectedLocation && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#f0f7ff' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#1565c0' }}>
            <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Selected Location Details
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">Name:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedLocation.name}</Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">ID:</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedLocation.id}</Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">External ID:</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedLocation.externalId}</Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">Description:</Typography>
            <Typography variant="body2">{selectedLocation.description}</Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">Logo:</Typography>
            <Typography variant="body2">{selectedLocation.logo}</Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Points Information ({selectedLocation.pointsInfo.length})
          </Typography>

          {selectedLocation.pointsInfo.map((point) => (
            <Paper key={point.index} sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Point #{point.index + 1}
              </Typography>

              <Box sx={{ pl: 2 }}>
                <Typography variant="body2">
                  <strong>Building:</strong> {point.building.name || 'N/A'} [{point.building.id || 'N/A'}]
                </Typography>
                <Typography variant="body2">
                  <strong>Floor:</strong> {point.floor.name || 'N/A'} [{point.floor.id || 'N/A'}]
                </Typography>
                <Typography variant="body2">
                  <strong>Elevation:</strong> {point.floor.elevation !== null ? point.floor.elevation : 'N/A'}
                </Typography>
                {point.coordinates && (
                  <Typography variant="body2">
                    <strong>Coordinates:</strong> Lat: {point.coordinates.latitude.toFixed(6)}, Lng: {point.coordinates.longitude.toFixed(6)}
                  </Typography>
                )}
              </Box>
            </Paper>
          ))}
        </Paper>
      )}

      {/* Buildings and Floors */}
      {buildingsData.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Buildings & Floors ({buildingsData.length})
          </Typography>

          {buildingsData.map((building) => (
            <Accordion key={building.id} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>{building.name}</Typography>
                  <Chip label={`${building.floors.length} floors`} size="small" color="primary" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  ID: {building.id}
                </Typography>

                {building.floors.map((floor) => (
                  <Paper key={floor.id} sx={{ p: 1.5, mb: 1, bgcolor: '#f5f5f5' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {floor.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {floor.id} | Elevation: {floor.elevation}
                    </Typography>
                  </Paper>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}

      {/* Enterprise Locations */}
      {locationsData.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            <LocationOnIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Enterprise Locations ({locationsData.length})
          </Typography>

          {locationsData.slice(0, 10).map((location) => (
            <Accordion key={location.id} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 600 }}>{location.name}</Typography>
                  {location.points.length > 0 && (
                    <Chip label={`${location.points.length} points`} size="small" color="success" />
                  )}
                  {location.spaces.length > 0 && (
                    <Chip label={`${location.spaces.length} spaces`} size="small" color="info" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  ID: {location.id} | External ID: {location.externalId || 'N/A'}
                </Typography>

                {location.points.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Points:</Typography>
                    {location.points.map((point, idx) => (
                      <Paper key={idx} sx={{ p: 1.5, mb: 1, bgcolor: '#f5f5f5' }}>
                        <Typography variant="body2">
                          <strong>Building:</strong> {point.buildingName || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Floor:</strong> {point.floorName || 'N/A'} (Elevation: {point.floorElevation})
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}

          {locationsData.length > 10 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Showing first 10 of {locationsData.length} locations. Check console for full data.
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default MappedInDataTest;
